// src/utils/formatters.js
import { flattenAppearances } from './crewSchedule';
import { FieldValue } from 'firebase/firestore'; // sentstrip-3f9a1c7d -- for stripFirestoreSentinels below

export const formatPhoneNumber = (value) => {
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
        return !match[2] ? match[1] : `(${match[1]}) ${match[2]}${match[3] ? '-' + match[3] : ''}`;
    }
    return value;
};

export const formatDate = (date) => {
  // fmtts-3d9a1e74 -- Coerce a Firestore Timestamp to its underlying JS Date
  // FIRST. This is the one shape the fallback below can't parse: a Timestamp is
  // truthy and not a Date instance, so it fell into the branch and new Date(ts)
  // → Invalid Date → 'N/A'. On the invoice detail view opened directly/refreshed,
  // invoice.createdAt / invoice.dueDate arrive as raw Timestamps and blanked to
  // 'N/A'. Only Firestore Timestamps carry .toDate() (JS Dates do not), so every
  // other input class — Date, string, number, null, undefined, garbage — skips
  // this line and hits the ORIGINAL logic byte-for-byte, unchanged.
  if (date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  if (!date || !(date instanceof Date)) {
    // Try to parse it if it's a string or timestamp
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return 'N/A';
    }
    return d.toLocaleDateString('en-US');
  }
  return date.toLocaleDateString('en-US'); // Formats to MM/DD/YYYY
};

// r2-span-tojsdate-4b8e1f07
// Coerce a Firestore Timestamp, a JS Date, or null/undefined into a JS Date
// (or null). This is the shared primitive behind date reads across the app:
// real jobs carry Timestamps; projected recurring rows carry plain Dates.
// Extracted from the duplicated idiom in JobTable/JobKanbanBoard so per-crew
// date logic (getJobDateSpan) and the formatters share ONE coercion path.
export const toJsDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();      // Firestore Timestamp
  if (value instanceof Date) return value;      // already a JS Date
  const d = new Date(value);                     // string / number fallback
  return isNaN(d.getTime()) ? null : d;
};

// jobnumdisp-6a1e9c34
// Format a job number for DISPLAY. Returns '' — never '#undefined' — when there
// is no number, so every call site degrades to exactly its pre-existing text.
//
// Two absences are normal and must both render as nothing:
//   1. setJobNumberOnCreate (functions/index.js:10706) is an onDocumentCreated
//      trigger that stamps the number in a SEPARATE transaction, so a freshly
//      saved job has no number for a few hundred ms — and none ever if that
//      transaction fails (it logs and does not retry).
//   2. Projected recurring occurrences have no job document at all; their
//      template lives in recurringJobs, which the trigger does not watch, and
//      jobNumber is excluded from RECURRING_PROPAGATE_FIELDS (index.js:3334).
//
// Stored values are zero-padded STRINGS ('0001', '1860'), so nothing is
// re-padded or coerced to a number here. The leading-'#' strip mirrors the
// defence ProfitabilityReport.js:330 already carries: some stored values
// arrived with the '#' baked in, and '##1860' would be the visible result.
export const formatJobNumber = (jobNumber) => {
  const s = jobNumber == null ? '' : String(jobNumber).trim().replace(/^#/, '');
  return s ? `#${s}` : '';
};

// r2-span-getspan-9c3d7a21
// A job's EFFECTIVE date span. Today every job has a single startAt/endAt and
// all crews share it — so this returns exactly that (a no-op vs current
// behavior). Once per-crew scheduling lands (Increment 1), a crews[] entry may
// carry its own startAt/endAt; then the span becomes MIN(start)..MAX(end)
// across crew portions and perCrew is populated. Centralizing this here means
// the job-date read-sites call ONE helper instead of reading job.startAt
// directly, so they never need to know whether a job is per-crew scheduled.
export const getJobDateSpan = (job) => {
  const flat = flattenAppearances(job?.crewSchedule)
    .map(a => ({ crewGroupId: a.crewGroupId, start: toJsDate(a.startAt), end: toJsDate(a.endAt) }))
    .filter(a => a.start);            // only appearances that carry a start

  if (flat.length === 0) {
    // Today's answer: the job's own top-level span (no per-crew scheduling).
    return {
      start: toJsDate(job?.startAt),
      end:   toJsDate(job?.endAt),
      isMultiCrewScheduled: false,
      perCrew: [],
    };
  }

  // Per-crew scheduled: envelope every dated appearance.
  const starts = flat.map(a => a.start.getTime());
  const ends   = flat.map(a => (a.end || a.start).getTime());

  // Group appearances back under their crew for perCrew.
  const byCrew = {};
  for (const a of flat) {
    (byCrew[a.crewGroupId] = byCrew[a.crewGroupId] || []).push(a);
  }
  const perCrew = Object.keys(byCrew).map(crewGroupId => ({
    crewGroupId,
    appearances: byCrew[crewGroupId],
  }));

  return {
    start: new Date(Math.min(...starts)),
    end:   new Date(Math.max(...ends)),
    isMultiCrewScheduled: true,
    perCrew,
  };
};

export const formatCurrency = (amount, currencyCode = 'USD') => {
  // Convert the incoming value to a number
  const number = parseFloat(amount);

  // If the result is not a valid number, default to 0
  const value = isNaN(number) ? 0 : number;

  // Otherwise, format the number as currency using the provided code
  return value.toLocaleString('en-US', { // 'en-US' locale is fine for formatting
    style: 'currency',
    currency: currencyCode, // <-- This is now dynamic
  });
};

export const parseLocalDate = (dateString) => {
    if (!dateString) return new Date(); // Fallback
    
    // Split the string to avoid UTC conversion
    const parts = dateString.split('-');
    if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-11
        const day = parseInt(parts[2], 10);
        return new Date(year, month, day);
    }
    
    // Fallback for any other format
    return new Date(dateString);
};

// localstamp-9f4e2a17 — Date → "YYYY-MM-DD" in LOCAL time; the exact inverse of
// parseLocalDate above. Uses getFullYear/getMonth/getDate (LOCAL getters) so the
// calendar day never shifts through UTC the way toISOString() does — otherwise
// loading the app after ~8pm Eastern stamps tomorrow's date. Call it fresh at the
// moment of stamping (never capture it at module load) so a tab left open overnight
// still stamps today's real local date. Defaults to now.
export const formatLocalDate = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// r3-paiddate — ONE source of truth for "when was this invoice paid".
// Reuses parseLocalDate + toJsDate above; no new date math.
//
// normalizePaidDate: coerce a stored paid-date value into a calendar-correct JS
// Date (or null). Two stored shapes exist:
//   • date-only "YYYY-MM-DD" (settledAt, and the manual Record-Payment date) —
//     built in LOCAL time via parseLocalDate, or it shifts a day: plain
//     new Date("2026-07-22") is UTC midnight → the day before in Toronto.
//   • full ISO / Timestamp / Date (the createQuickBooksPayment fallback) — a
//     real instant; toJsDate coerces it.
export const normalizePaidDate = (value) => {
  if (!value) return null;
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return parseLocalDate(s); // date-only → local
  return toJsDate(value);                                       // instant → coerce
};

// isQuickBooksOriginInvoice: did this invoice ORIGINATE in QuickBooks? True for
// invoices imported FROM a client's QuickBooks file (the GC Septic migration):
// doc IDs 'qbinv_<QB No.>' and importId 'gcseptic_qb_migration_v1'. Those
// already exist in QuickBooks, so the UI shows an "In QuickBooks" badge instead
// of an export button. Deliberately NOT keyed on source === 'import' (that only
// means "not created in-app"; a future Jobber import would match too and those
// SHOULD stay pushable).
// MIRROR: keep in sync with isQuickBooksOriginInvoice in functions/index.js
export const isQuickBooksOriginInvoice = (invoiceId, invoice) => { // qbguard-4fa1e6b2
  if (invoiceId && String(invoiceId).startsWith('qbinv_')) return true;
  if (invoice && invoice.importId === 'gcseptic_qb_migration_v1') return true;
  return false;
};

// impro-3c8f1a57 -- isImportedInvoice: was this invoice brought in from another system
// (GC Septic's QuickBooks migration, Alberni's Jobber import, the import wizard)? Imported
// invoices are history (founder decision 2026-10-15): no editing of lines, totals, tax or
// status. Payments (Record Payment / Apply Credit) and Notes & Media still work.
// Both fields are set once at import and never edited.
// MIRROR: keep in sync with isImportedInvoice in functions/index.js and in
// spatchy-mobile/src/screens/invoices/InvoiceBuilderScreen.js
export const isImportedInvoice = (invoice) => !!(invoice && (invoice.importId || invoice.importedAt));

// qbpaysync-b4d7e219 -- SINGLE SOURCE OF TRUTH for the payment-sync warning wording,
// shared by PaymentModal (Record Payment, mode 'record') and InvoiceFormModal
// (status-settle, mode 'settle'). Same predicate, opposite conclusion for a LINKED
// invoice: Record Payment syncs it (no warning); status-settle does not (warn).
// Returns the plain-English warning string, or null when it will sync / the org is
// not on QuickBooks. The persisted qbPaymentSyncError mark stays generic; these three
// strings are only the user-facing advice.
export const getPaymentSyncWarning = (invoice, quickbooksConnected, mode = 'record') => {
  if (!invoice || !quickbooksConnected) return null;
  const linked = !!(invoice.qbInvoiceId && invoice.customerDetails?.qbId);
  if (mode === 'record' && linked) return null; // Record Payment will sync a linked invoice
  if (isQuickBooksOriginInvoice(invoice.id, invoice)) {
    return 'This invoice came from QuickBooks and is not linked to it here. Recording a payment in Spatchy will not update QuickBooks, so the invoice will still show as unpaid there. Record this payment in QuickBooks too, so your books match.';
  }
  if (linked) {
    return 'This invoice is linked to QuickBooks. Marking it paid with the status dropdown will not record the payment there, so QuickBooks will still show it as unpaid. Use the Record Payment button instead, which sends the payment to QuickBooks, or add the payment in QuickBooks yourself.';
  }
  return 'This invoice has not been sent to QuickBooks yet, so a payment you record here will not appear there. Export the invoice to QuickBooks first, then record the payment there. Note: exporting an invoice does not carry its payments across, so always export before recording the payment.';
};

// paysyncpred-4b8e1a07 -- single source of truth for whether an invoice's persisted
// payment-sync mark is currently ACTIVE (unhandled). Compared by VALUE, not time: on
// dismiss the invoice's current qbPaymentSyncErrorAt is copied into
// qbPaymentSyncAckedErrorAt, so any NEW mark (which writes a different
// qbPaymentSyncErrorAt) can never equal the acked value and the warning reappears --
// no cross-clock timestamp comparison. A mark with no qbPaymentSyncErrorAt is malformed
// (all three writers set both fields together) and is treated as NOT active, so a
// dismiss always captures a real value. Reads raw persisted fields only, so it is NOT a
// derived row field and needs no INVOICE_ROW_DERIVED_KEYS entry.
export const isPaymentSyncWarningActive = (invoice) => {
  const inv = invoice || {};
  if (!inv.qbPaymentSyncError) return false;
  if (!inv.qbPaymentSyncErrorAt) return false;
  return inv.qbPaymentSyncErrorAt !== inv.qbPaymentSyncAckedErrorAt;
};

// qbrel-2e9b4c17 -- compact relative-time for the QuickBooks health sub-line
// ("3 minutes ago"). Returns null for an absent/unparseable value so the caller
// omits the clause rather than rendering "Invalid Date". Reuses toJsDate for the
// Timestamp/Date/string coercion; falls back to an absolute date past ~30 days.
// qview-6b2d4e91 — exported for Seam 10's "last viewed 3 hours ago" line.
export const formatRelativeTime = (value) => {
  const then = toJsDate(value);
  if (!then) return null;
  const diffMs = Date.now() - then.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return `on ${formatDate(then)}`;
};

// qbhealth-7c3f9a21 -- SINGLE SOURCE OF TRUTH for the QuickBooks connection health
// DISPLAY state, mirroring the isPaymentSyncWarningActive convention above. Reads the
// organizations/{orgId} snapshot and derives which of five states the Settings ->
// Integrations QuickBooks card renders. EVERY field is optional and degrades to
// omission, never a blank label or "Invalid Date":
//   - an org connected BEFORE Seam B shipped has no quickbooksHealthStatus and no
//     quickbooksLastHealthCheck -> 'never_verified', NOT 'broken';
//   - an old connection may lack quickbooksTokenExpiresAt -> the renews clause is dropped;
//   - a never-refreshed connection has no quickbooksLastRefreshed (unused here, but the
//     same absence-is-not-an-error rule applies to every clause).
// Returns { state, tone, label, subLine, reason }:
//   state:   'healthy' | 'never_verified' | 'broken' | 'inconclusive' | 'disconnected'
//   tone:    pill color the card maps to classes ('green'|'gray'|'red'|'amber'|'neutral')
//   label:   pill text (null for 'disconnected' -> no pill, just the Connect button)
//   subLine: descriptive line under the header (null when there's nothing to add)
//   reason:  red reconnect-reason line for the 'broken' state (null otherwise)
export const resolveQuickBooksHealth = (org) => {
  const o = org || {};
  const connected = o.quickbooksConnected === true;
  const status = o.quickbooksHealthStatus;
  const disconnectReason = o.quickbooksDisconnectReason;

  // Broken takes precedence: an auth failure the health check recorded, OR a boolean the
  // token gateway flipped false while leaving a death reason behind. This is the important
  // new surface -- a silently-died connection used to show a plain Connect button with no
  // explanation. The reason clause omits the em-dash tail when no stored reason exists.
  if (status === 'auth_failed' || (!connected && disconnectReason)) {
    return {
      state: 'broken',
      tone: 'red',
      label: 'Connection problem',
      subLine: null,
      reason: disconnectReason
        ? `QuickBooks needs to be reconnected — ${disconnectReason}`
        : 'QuickBooks needs to be reconnected to resume syncing.',
    };
  }

  if (connected && status === 'healthy') {
    const clauses = [];
    const verified = formatRelativeTime(o.quickbooksLastHealthCheck);
    if (verified) clauses.push(`Last verified ${verified}`);
    const renews = toJsDate(o.quickbooksTokenExpiresAt);
    if (renews) clauses.push(`Access renews ${formatDate(renews)}`);
    return {
      state: 'healthy',
      tone: 'green',
      label: 'Connected',
      subLine: clauses.length ? clauses.join(' · ') : null,
      reason: null,
    };
  }

  if (connected && status === 'unknown') {
    return {
      state: 'inconclusive',
      tone: 'amber',
      label: "Couldn't verify",
      subLine: "Last check was inconclusive — we'll retry automatically.",
      reason: null,
    };
  }

  // Connected but never health-checked (no lastHealthCheck), OR connected with a stamp but
  // an unrecognized status: both degrade to the same safe "not yet verified" surface -- an
  // org connected before Seam B shipped lands here, and no path shows a blank pill.
  if (connected) {
    return {
      state: 'never_verified',
      tone: 'gray',
      label: 'Connected — not yet verified',
      subLine: "We haven't confirmed this connection yet. Click Test connection to check it.",
      reason: null,
    };
  }

  // Not connected, no death reason -> the plain first-time Connect state (current behavior).
  return { state: 'disconnected', tone: 'neutral', label: null, subLine: null, reason: null };
};

// INVOICE_ROW_DERIVED_KEYS: the view-only fields the invoice-list mapper
// synthesizes onto each row (qbPushState via deriveInvoiceRowFields below;
// paidDate via getInvoicePaidDate). They must NEVER be written back to an
// invoice document -- the QB status column added them and edit-save was leaking
// them through setDoc(merge:true). InvoiceFormModal.handleSave strips exactly
// these keys before writing. HAND-MAINTAINED ON PURPOSE: if you add a synthetic
// field to the mapper, add its name here too. Do NOT compute this from the
// helper's keys -- a future conditional key would silently drop out of the probe
// and stop being stripped while still looking self-maintaining. strip-a1c4e097
export const INVOICE_ROW_DERIVED_KEYS = ['qbPushState', 'paidDate'];

// h1inv-6d3a9f27 — H1 Seam 2: invoice fields only the server writes (firestore.rules
// invoiceServerFieldNames() refuses any client add/change/removal). The editor loads the whole
// invoice and used to echo these back; an echo of a value the server changed while the form was
// open (a payment landing, a QuickBooks sync) would overwrite it — or, now, get the save refused.
// InvoiceFormModal strips them from every save. Keep in step with the rules list.
export const INVOICE_SERVER_OWNED_KEYS = [
    'invoiceNumber', 'paymentHistory', 'paidAt',
    'qbInvoiceId', 'qbInvoiceNumber', 'qbStatus', 'lastSyncedAt',
    'qbSyncState', 'qbSyncError', 'qbSyncErrorAt', 'qbSyncAttempts', 'qbSyncClaimAt', 'qbSyncClaimBy',
    'qbSyncSweepAttempts', 'qbSyncLastSweepAt', 'qbSyncSweepExhaustedAt',
    'qbPushError', 'qbPushErrorAt', 'qbAutoSyncDeferred', 'qbAutoSyncDeferredAt', 'qbAutoSyncDeferredReason',
    'qbTotalMismatch', 'sentAt', 'sentBy', 'sentTo', 'pastDueAt', 'lastReminderSent',
    'remindersMuted', 'remindersMutedAt', 'remindersMutedReason', 'billingPeriodStart', 'billingPeriodEnd',
    'source', 'importId', 'importedAt', 'importedBy', 'importNotes', 'legacyInvoiceNumber', 'salesPersonId',
    'taxExemptionOff', 'taxExemptionHistory', 'qbTaxCodeHold', // taxexempt3-8c4f2a1d — server-owned (switch-off, QuickBooks hold)
];

// sentstrip-8b4e2a61 -- strip Firestore FieldValue sentinels (serverTimestamp /
// deleteField / etc.) out of an object before it enters React state. A sentinel
// is a live write-token, not a value; rendering one throws "Objects are not valid
// as a React child (found: object with keys {_methodName})". handleSave in the
// Invoice/Quote form modals hands its WRITE object to closeModal -> parent
// setState; the write must keep the sentinels (Firestore needs them), the state
// copy must not. Detects by TYPE (instanceof FieldValue -- verified a named export
// at firebase 11.10.0) so any current OR future sentinel is caught, and maps each
// to null. null (not key omission) is required: the parent merges {...prev,...x},
// so only an explicit null overwrites a stale prior value. Shallow by design --
// every sentinel these handlers write sits at the object's top level.
export const stripFirestoreSentinels = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const key of Object.keys(obj)) {
    out[key] = obj[key] instanceof FieldValue ? null : obj[key];
  }
  return out;
};

// deriveInvoiceRowFields: home for the invoice-list mapper's synthesized row
// fields, shared so the list and the edit-save strip agree on one source of
// truth. Today it returns only qbPushState (paidDate stays inline in the mapper
// for an unrelated reason), hence the plural name anticipating consolidation;
// its key is still registered in INVOICE_ROW_DERIVED_KEYS above. qbPushState is
// NOT data.qbStatus (the customer's QB payment status on imported rows, one char
// away). Precedence origin > synced > failed > deferred: a successful push clears
// the error fields, but if both were somehow present, synced wins. qbdefer-3f8a1c92
// -- 'deferred' (Seam A) is an automatic sync BLOCKED on a recoverable condition
// (dead connection / accounts not configured); the backend sets qbAutoSyncDeferred
// and clears it on any successful push. It ranks BELOW failed (a real red failure
// is more urgent than "waiting to sync") and degrades to 'none' when the marker is
// absent -- which is every invoice created before auto-sync existed. UI-only; never
// persisted. On success the backend deletes the marker so this reverts to synced.
export const deriveInvoiceRowFields = (invoiceId, invoice) => {
  const inv = invoice || {};
  return {
    qbPushState: isQuickBooksOriginInvoice(invoiceId, inv) ? 'origin' : (inv.qbInvoiceId ? 'synced' : (inv.qbPushError ? 'failed' : (inv.qbAutoSyncDeferred === true ? 'deferred' : 'none'))),
  };
};

// getInvoicePaidDate: the invoice's paid date as a JS Date, or null.
// Priority: real recorded payments first, then a status-settle. Imported-paid
// invoices (no paymentHistory, no settledViaStatus) return null → callers render
// the em-dash. createdAt is deliberately NOT a fallback.
export const getInvoicePaidDate = (invoice) => {
  if (!invoice) return null;

  // (1) Record Payment — paymentHistory[].date. Multiple payments → most recent.
  const history = Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory : [];
  const paidDates = history
    .map((p) => normalizePaidDate(p && p.date))
    .filter(Boolean);
  if (paidDates.length > 0) {
    return new Date(Math.max(...paidDates.map((d) => d.getTime())));
  }

  // (2) Status-dropdown mark-paid — settledAt, ONLY when the marker is true.
  if (invoice.settledViaStatus === true) {
    return normalizePaidDate(invoice.settledAt);
  }

  // (3) Imported / unpaid / pre-fix inconsistent → no owned paid date.
  return null;
};

// creditSourceLabel: an account credit's origin as a human-readable source
// ("Recorded payment", "Deposit", "Credit memo", "Overpayment", "Imported
// credit") instead of the raw internal enum. Shared by the Account Credits list
// on the customer record and the Apply-Credit picker so a credit reads the same
// in both places. Keyed on c.origin only — never on type/memo (raw values).
export const creditSourceLabel = (c) => {
  switch (c && c.origin) {
    case 'quoteDeposit': return 'Deposit';
    case 'creditMemo': return 'Credit memo';
    case 'overpayment': return 'Overpayment';
    case 'import':
    case 'remainderMigration': return 'Imported credit';
    case 'manual':
    case 'payment': return 'Recorded payment';
    default: return 'Account credit';
  }
};